import { Factory, Ship, Globe, GraduationCap, BadgeCheck, MapPin, ArrowUpRight } from "lucide-react";
import { FadeIn, SectionHeading } from "./Reveal";

const IMG = {
  manufacturing:
    "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZmFjdG9yeSUyMG1hbnVmYWN0dXJpbmclMjBtYWNoaW5lcnl8ZW58MHx8fHwxNzkwMjM1OTg5fDA&ixlib=rb-4.1.0&q=85&w=1400&auto=format",
  export:
    "https://images.unsplash.com/photo-1759272840538-ae4b07214c71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxjYXJnbyUyMHNoaXBwaW5nJTIwY29udGFpbmVycyUyMGV4cG9ydCUyMHBvcnR8ZW58MHx8fHwxNzkwMjM1OTg5fDA&ixlib=rb-4.1.0&q=85&w=1400&auto=format",
  import:
    "https://images.unsplash.com/photo-1601897690942-bcacbad33e55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXBwaW5nJTIwY29udGFpbmVycyUyMGV4cG9ydCUyMHBvcnR8ZW58MHx8fHwxNzkwMjM1OTg5fDA&ixlib=rb-4.1.0&q=85&w=1000&auto=format",
  marketing:
    "https://images.unsplash.com/photo-1620306677888-10e367e6293d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXBwaW5nJTIwY29udGFpbmVycyUyMGV4cG9ydCUyMHBvcnR8ZW58MHx8fHwxNzkwMjM1OTg5fDA&ixlib=rb-4.1.0&q=85&w=1000&auto=format",
  bd:
    "https://images.unsplash.com/photo-1578575437130-527eed3abbec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHNoaXBwaW5nJTIwY29udGFpbmVycyUyMGV4cG9ydCUyMHBvcnR8ZW58MHx8fHwxNzkwMjM1OTg5fDA&ixlib=rb-4.1.0&q=85&w=1400&auto=format",
};

const Tile = ({ img, icon: Icon, title, desc, className = "", tall = false }) => (
  <div
    data-testid="operation-tile"
    className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] transition-all duration-300 hover:border-brand-red/40 hover:shadow-[0_0_40px_rgba(220,38,38,0.15)] ${className}`}
  >
    {img && (
      <img
        src={img}
        alt={title}
        className="absolute inset-0 h-full w-full object-cover opacity-40 transition-all duration-700 group-hover:scale-105 group-hover:opacity-55"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/10" />
    <div className={`relative flex h-full flex-col p-6 sm:p-8 ${tall ? "justify-end" : "justify-between"}`}>
      <div className="flex items-start justify-between">
        <span className="rounded-xl border border-brand-red/40 bg-brand-red/10 p-3 text-brand-bright backdrop-blur-sm">
          <Icon size={22} />
        </span>
        <ArrowUpRight className="text-slate-500 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-brand-red" size={20} />
      </div>
      <div className={tall ? "" : "mt-10"}>
        <h3 className="font-display text-xl font-semibold text-white sm:text-2xl">{title}</h3>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-400">{desc}</p>
      </div>
    </div>
  </div>
);

export const Operations = () => (
  <section id="operations" className="relative bg-surface py-20 sm:py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        kicker="02 — Our Operations"
        title="Four verticals. One accountable group."
        description="Every vertical below runs on the same documented systems — which is exactly what a candidate should check before joining any company."
      />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-4 lg:grid-rows-[repeat(3,minmax(230px,auto))]">
        <FadeIn className="md:col-span-2 lg:row-span-2">
          <Tile
            img={IMG.manufacturing}
            icon={Factory}
            title="Manufacturing"
            desc="High-capacity processing facilities with automated lines, in-house quality labs and batch-level traceability under ISO 9001:2015 systems."
            className="h-full min-h-[420px] lg:min-h-full"
            tall
          />
        </FadeIn>
        <FadeIn delay={0.08} className="md:col-span-2">
          <Tile
            img={IMG.export}
            icon={Ship}
            title="Distribution"
            desc="Import, export, and distribution services worldwide — handled end-to-end from our Kerala base."
            className="h-full min-h-[240px]"
          />
        </FadeIn>
        <FadeIn delay={0.16}>
          <Tile
            icon={Globe}
            title="Online"
            desc="Bringing Techworld products and services to customers through digital channels."
            className="h-full min-h-[240px]"
          />
        </FadeIn>
        <FadeIn delay={0.24}>
          <Tile
            icon={GraduationCap}
            title="Institute of Management Studies"
            desc="Our in-house management institute — training, development and direct recruitment at the director's desk."
            className="h-full min-h-[240px]"
          />
        </FadeIn>
        <FadeIn delay={0.3} className="md:col-span-2">
          <Tile
            icon={BadgeCheck}
            title="Quality Management Systems"
            desc="Certified frameworks that effectively oversee operations — superior quality and reliability across every vertical."
            className="h-full min-h-[240px]"
          />
        </FadeIn>
        <FadeIn delay={0.36} className="md:col-span-2">
          <div className="flex h-full min-h-[240px] flex-col justify-between rounded-2xl border border-brand-red/40 bg-brand-red/[0.08] p-6 sm:p-8" data-testid="operation-tile">
            <MapPin className="text-brand-red" size={26} />
            <div>
              <h3 className="font-display text-xl font-semibold text-white sm:text-2xl">
                Headquartered in Alappuzha
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                Five major branches — Alappuzha (HQ), Cherthala, Aroor,
                Ambalappuzha and Kottayam — anchor a network of 67+ branches
                across Kerala. Walk in and see us for yourself.
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

export default Operations;
